import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/ui/StatusBadge';
import Toast from '@/components/ui/Toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { CatalogRow } from '@/lib/catalog';
import { usePerms } from '@/lib/perms';
import { specsApi, type CostKind, type SpecFull, type SpecLine, type SpecStatus } from '@/lib/specs';
import { aed, num, shortDate } from '@/lib/format';

type ToastState = { message: string; detail?: string; tone?: 'success' | 'error' } | null;
const COST_KINDS: CostKind[] = ['pickup', 'delivery', 'installation', 'other'];
const btnGhost = 'inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-default disabled:opacity-50';
const btnPrimary = 'inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-default disabled:opacity-50';
const inputCls = 'h-8 w-full rounded border border-background-200 bg-background-50 px-2 text-[12px] text-foreground-900 outline-none focus:border-primary-400 disabled:bg-background-100';

/** Спецификация на данных сервера (этап 1.3): строки со снимками себестоимости (FIFO по партиям) и цены (наценка по правилу,
 * округление, минимум партнёра — проверяется в БД), прямые расходы план/факт по тарифам с ручным переопределением,
 * товарная маржа и вклад после прямых расходов, согласования ниже порога, статусы Draft → Quoted → Won/Lost. */
export default function SpecificationDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { me } = useAuth();
  const { can, admin } = usePerms();
  const canEdit = can('specifications', 'write');
  const [spec, setSpec] = useState<SpecFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const [activeId, setActiveId] = useState<string>('');
  const [alts, setAlts] = useState<CatalogRow[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [header, setHeader] = useState({ client: '', project: '', siteAddress: '', deliveryDate: '' });
  const [quickSku, setQuickSku] = useState('');
  const [reason, setReason] = useState('');
  const [showPo, setShowPo] = useState(false);
  const [costDraft, setCostDraft] = useState<Record<string, string>>({});

  const apply = useCallback((s: SpecFull) => {
    setSpec(s);
    setHeader({ client: s.client ?? '', project: s.project ?? '', siteAddress: s.siteAddress ?? '', deliveryDate: s.deliveryDate ?? '' });
    setActiveId((cur) => (s.lines.some((l) => l.id === cur) ? cur : s.lines[0]?.id ?? ''));
  }, []);

  useEffect(() => {
    if (!id) return;
    specsApi
      .get(id)
      .then(apply)
      .catch((e) => setError((e as ApiError).code));
  }, [id, apply]);

  const activeLine = useMemo(() => spec?.lines.find((l) => l.id === activeId) ?? null, [spec, activeId]);
  useEffect(() => {
    if (!id || !activeLine) {
      setAlts([]);
      return;
    }
    let alive = true;
    specsApi
      .alternatives(id, activeLine.sku)
      .then((r) => alive && setAlts(r))
      .catch(() => alive && setAlts([]));
    return () => {
      alive = false;
    };
  }, [id, activeLine?.sku]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn: () => Promise<SpecFull>, okMessage?: string) => {
    setBusy(true);
    try {
      apply(await fn());
      if (okMessage) setToast({ message: okMessage });
      return true;
    } catch (e) {
      const code = (e as ApiError).code;
      setToast({ message: code === 'below_min_resale' ? t('sp.belowMinResale') : code.startsWith('blocked:') ? t('sp.blocked', { list: code.slice(8).split(',').map((b) => t(`sp.blocker.${b}`, { defaultValue: b })).join(', ') }) : t('ui.error'), detail: code, tone: 'error' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
        <p className="text-sm font-medium text-foreground-900">{error === 'not_found' ? t('specDetail.notFound') : t('onb.error', { code: error })}</p>
        <Link to="/specifications" className="mt-2 inline-block text-[12px] font-semibold text-primary-700">
          {t('specDetail.backToSpec')}
        </Link>
      </div>
    );
  }
  if (!spec || !id) return <div className="p-8 text-center text-[12.5px] text-foreground-500">{t('ui.loading')}</div>;

  const locked = spec.status === 'Won' || spec.status === 'Lost' || spec.status === 'Cancelled';
  const editable = canEdit && !locked;
  const m = spec.metrics;
  const shortLines = spec.lines.filter((l) => l.short > 0);
  const key = (lid: string, k: string) => `${lid}:${k}`;
  const lineOf = (lid: string) => spec.lines.find((l) => l.id === lid);

  const saveHeader = (k: keyof typeof header) => {
    const cur = { client: spec.client ?? '', project: spec.project ?? '', siteAddress: spec.siteAddress ?? '', deliveryDate: spec.deliveryDate ?? '' };
    if (header[k] === cur[k]) return;
    void run(() => specsApi.patch(id, { [k]: header[k] || null } as never));
  };
  const commitLine = (lid: string, k: 'qty' | 'price') => {
    const raw = draft[key(lid, k)];
    if (raw === undefined) return;
    setDraft((d) => {
      const n = { ...d };
      delete n[key(lid, k)];
      return n;
    });
    const line = lineOf(lid);
    if (!line) return;
    if (k === 'qty') {
      const q = Math.max(1, Math.round(Number(raw) || 0));
      if (q !== line.qty) void run(() => specsApi.patchLine(id, lid, { qty: q }));
    } else {
      const v = raw.trim() === '' ? null : Number(raw);
      if (v === null && !line.manualPrice) return;
      if (v !== null && v === line.sale && line.manualPrice) return;
      void run(() => specsApi.patchLine(id, lid, { price: v }));
    }
  };
  const quickAdd = async () => {
    const sku = quickSku.trim();
    if (!sku) return;
    if (await run(() => specsApi.addLine(id, { sku, qty: 1 }), t('sp.lineAdded', { sku }))) setQuickSku('');
  };
  const swap = async (lineId: string, sku: string) => {
    const line = lineOf(lineId);
    if (!line) return;
    const ok = await run(async () => {
      await specsApi.removeLine(id, lineId);
      return specsApi.addLine(id, { sku, qty: line.qty });
    }, t('specDetail.toastSwapped', { sku }));
    if (ok) setActiveId('');
  };
  const commitCost = (kind: CostKind, stage: 'plan' | 'actual') => {
    const k = `${stage}:${kind}`;
    const raw = costDraft[k];
    if (raw === undefined) return;
    setCostDraft((d) => {
      const n = { ...d };
      delete n[k];
      return n;
    });
    const cur = spec.costs[stage].find((c) => c.kind === kind);
    const v = raw.trim() === '' ? null : Number(raw);
    if (v === null && (!cur || (stage === 'plan' && !cur.overridden))) return;
    if (v !== null && cur && v === cur.amount && (stage === 'actual' || cur.overridden)) return;
    void run(() => specsApi.setCost(id, { kind, stage, amount: v }));
  };
  const canDecide = (level: string) => (level === 'loss' ? Boolean(me?.canApproveLoss) : admin || Boolean(me?.canApproveMargin) || Boolean(me?.canApproveLoss));
  const statusLabel = (s: SpecStatus) => t(`sp.go.${s}`);
  const pct = (v: number | null) => (v == null ? '—' : `${v.toFixed(1)}%`);
  const costLabel = (k: CostKind) => t(`sp.cost.${k}`);

  return (
    <div>
      <Link to="/specifications" className="mb-3 inline-flex items-center gap-1 text-[11.5px] font-medium text-foreground-500 transition-colors hover:text-foreground-900">
        <i className="ri-arrow-left-line text-[12px]" /> {t('specDetail.back')}
      </Link>

      <div className="flex flex-col gap-4 border-b border-background-200 pb-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="tnum rounded border border-background-300 bg-background-50 px-1.5 py-0.5 text-[10.5px] font-medium text-foreground-600">{spec.code}</span>
            <StatusBadge tone={spec.status === 'Won' ? 'primary' : spec.status === 'Quoted' ? 'secondary' : spec.status === 'Draft' ? 'neutral' : 'danger'}>{t(`status.${spec.status}`, spec.status)}</StatusBadge>
            {spec.ownerName && <span className="text-[11.5px] text-foreground-500">{t('specDetail.owner', { name: spec.ownerName })}</span>}
            <span className="tnum text-[10.5px] text-foreground-400">v{spec.version}</span>
            {spec.quoteValidUntil && <span className="text-[10.5px] text-foreground-400">{t('sp.quoteValid', { date: shortDate(spec.quoteValidUntil) })}</span>}
          </div>
          <h1 className="mt-1.5 text-lg font-semibold text-foreground-950 md:text-xl">{spec.project || t('spec.untitled')}</h1>
          <p className="mt-0.5 text-[12px] text-foreground-500">{t('specDetail.deliveryMeta', { client: spec.client || '—', date: spec.deliveryDate ? shortDate(spec.deliveryDate) : '—' })}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && spec.frozen && !locked && (
            <button type="button" disabled={busy} onClick={() => void run(() => specsApi.reprice(id), t('sp.repriced'))} className={btnGhost} title={t('sp.repriceHint')}>
              <i className="ri-refresh-line text-[14px]" /> {t('sp.reprice')}
            </button>
          )}
          {canEdit &&
            (['Draft', 'Quoted', 'Won', 'Lost', 'Cancelled'] as SpecStatus[])
              .filter((s) => spec.transitions.includes(s) || (spec.blockers[s] ?? []).length > 0)
              .map((s) => {
                const blocked = (spec.blockers[s] ?? []).length > 0;
                const primary = s === 'Quoted' || s === 'Won';
                return (
                  <button key={s} type="button" disabled={busy || blocked} onClick={() => void run(() => specsApi.setStatus(id, s), t('sp.statusChanged', { status: t(`status.${s}`, s) }))} className={primary ? btnPrimary : btnGhost} title={blocked ? t('sp.blocked', { list: spec.blockers[s].map((b) => t(`sp.blocker.${b}`, { defaultValue: b })).join(', ') }) : undefined}>
                    <i className={`${s === 'Quoted' ? 'ri-send-plane-line' : s === 'Won' ? 'ri-trophy-line' : s === 'Draft' ? 'ri-arrow-go-back-line' : 'ri-close-circle-line'} text-[14px]`} /> {statusLabel(s)}
                  </button>
                );
              })}
          <button type="button" onClick={() => setShowPo(true)} className={btnGhost}>
            <i className="ri-shopping-bag-3-line text-[14px]" /> {t('specDetail.createPo')}
          </button>
        </div>
      </div>

      {locked && <p className="mt-3 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-[11.5px] text-foreground-600">{t('sp.lockedNote')}</p>}
      {spec.frozen && !locked && <p className="mt-3 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-[11.5px] text-secondary-900">{t('sp.frozenNote')}</p>}

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="flex flex-col gap-4 xl:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(
                [
                  ['client', t('specDetail.client')],
                  ['project', t('specDetail.project')],
                  ['siteAddress', t('sp.siteAddress')],
                ] as const
              ).map(([k, label]) => (
                <label key={k} className="block min-w-0">
                  <span className="mb-1 block text-[10px] font-medium uppercase tracking-[0.08em] text-foreground-400">{label}</span>
                  <input value={header[k]} disabled={!canEdit} onChange={(e) => setHeader((h) => ({ ...h, [k]: e.target.value }))} onBlur={() => saveHeader(k)} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} className={inputCls} />
                </label>
              ))}
              <label className="block min-w-0">
                <span className="mb-1 block text-[10px] font-medium uppercase tracking-[0.08em] text-foreground-400">{t('specDetail.deliveryDate')}</span>
                <input type="date" value={header.deliveryDate} disabled={!canEdit} onChange={(e) => setHeader((h) => ({ ...h, deliveryDate: e.target.value }))} onBlur={() => saveHeader('deliveryDate')} className={`tnum ${inputCls}`} />
              </label>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="flex flex-col gap-2 border-b border-background-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-foreground-950">{t('specDetail.linesTitle')}</h2>
                <p className="mt-0.5 max-w-xl text-[11px] leading-4 text-foreground-500">{t('specDetail.linesSub')}</p>
              </div>
              {editable && (
                <div className="flex items-center gap-2">
                  <input value={quickSku} onChange={(e) => setQuickSku(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void quickAdd()} placeholder={t('sp.quickAddSku')} aria-label={t('sp.quickAddSku')} className="h-7 w-36 rounded border border-background-200 bg-background-50 px-2 text-[11.5px] outline-none focus:border-primary-400" />
                  <button type="button" disabled={busy || !quickSku.trim()} onClick={() => void quickAdd()} className="h-7 cursor-pointer whitespace-nowrap rounded border border-background-300 px-2.5 text-[11px] font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-50">
                    <i className="ri-add-line text-[12px]" /> {t('sp.add')}
                  </button>
                  <Link to="/catalog" className="inline-flex h-7 w-fit items-center gap-1 whitespace-nowrap rounded border border-background-300 px-2.5 text-[11px] font-medium text-foreground-700 hover:bg-background-100">
                    <i className="ri-grid-line text-[12px]" /> {t('specDetail.addItem')}
                  </Link>
                </div>
              )}
            </div>

            <div className="overflow-x-auto thin-scroll">
              <table className="w-full min-w-[1040px] border-collapse text-[12px]">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/70 text-left text-[10px] uppercase tracking-[0.07em] text-foreground-500">
                    <th className="px-4 py-2 font-medium">{t('specDetail.colItem')}</th>
                    <th className="px-2 py-2 font-medium">{t('specDetail.colSource')}</th>
                    <th className="px-2 py-2 text-center font-medium">{t('specDetail.colQty')}</th>
                    <th className="px-2 py-2 font-medium">{t('specDetail.colAvailable')}</th>
                    <th className="px-2 py-2 font-medium">{t('specDetail.colOption')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('specDetail.colCost')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('specDetail.colSale')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('specDetail.colLineMargin')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('sp.colContribution')}</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {spec.lines.map((line: SpecLine) => {
                    const isActive = activeId === line.id;
                    const optionList = line.options.length > 0 ? line.options : line.option ? [{ label: line.option, surcharge: line.optionSurcharge }] : [];
                    return (
                      <tr key={line.id} onClick={() => setActiveId(line.id)} className={`cursor-pointer border-b border-background-200/70 transition-colors last:border-0 ${isActive ? 'bg-primary-50/50' : 'hover:bg-background-100/60'}`}>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded border border-background-200 bg-white">
                              {line.image ? <img src={line.image} alt={line.name} className="h-full w-full object-contain" /> : <i className="ri-image-line text-[14px] text-foreground-300" />}
                            </div>
                            <div className="min-w-0 max-w-[230px]">
                              <p className="tnum text-[10.5px] text-foreground-500">
                                <Link to={`/catalog/${encodeURIComponent(line.sku)}`} onClick={(e) => e.stopPropagation()} className="hover:text-primary-700">
                                  {line.sku}
                                </Link>
                                {line.cbm == null && <span className="ml-1 rounded bg-accent-100 px-1 text-[9.5px] font-medium text-accent-900">{t('sp.noCbmTag')}</span>}
                              </p>
                              <p className="truncate font-medium text-foreground-900">{line.name}</p>
                              {line.priceBasis && <p className="text-[10px] text-foreground-400">{t(`sp.priceBasis.${line.priceBasis}`)}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${line.source === 'Own' ? 'border-primary-200 bg-primary-50 text-primary-800' : 'border-secondary-200 bg-secondary-50 text-secondary-800'}`}>{line.source === 'Own' ? t('specDetail.own') : line.partnerName}</span>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            min={1}
                            disabled={!editable}
                            value={draft[key(line.id, 'qty')] ?? line.qty}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setDraft((d) => ({ ...d, [key(line.id, 'qty')]: e.target.value }))}
                            onBlur={() => commitLine(line.id, 'qty')}
                            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                            aria-label={`${t('specDetail.colQty')} ${line.sku}`}
                            className="tnum h-6 w-14 rounded border border-background-200 bg-background-50 px-1 text-center text-[11.5px] outline-none focus:border-primary-400 disabled:bg-transparent"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <span className={`tnum ${line.short > 0 ? 'font-semibold text-accent-800' : 'text-foreground-600'}`}>{line.available}</span>
                          {line.short > 0 && (
                            <span className="mt-0.5 flex items-center gap-1 text-[10px] font-medium text-accent-800">
                              <i className="ri-alert-line text-[10px]" /> {t('specDetail.short', { count: line.short })}
                            </span>
                          )}
                          {line.incoming > 0 && <span className="block text-[10px] text-foreground-400">+{line.incoming}</span>}
                        </td>
                        <td className="px-2 py-2">
                          {optionList.length > 0 ? (
                            <select value={line.option ?? ''} disabled={!editable} onClick={(e) => e.stopPropagation()} onChange={(e) => void run(() => specsApi.patchLine(id, line.id, { option: e.target.value || null }))} aria-label={`${t('specDetail.colOption')} ${line.sku}`} className="h-6 max-w-[150px] cursor-pointer rounded border border-background-200 bg-background-50 px-1 text-[11px] text-foreground-700 outline-none focus:border-primary-400 disabled:cursor-default">
                              <option value="">—</option>
                              {optionList.map((o) => (
                                <option key={o.label} value={o.label}>
                                  {o.label}
                                  {o.surcharge > 0 ? ` +${o.surcharge}` : ''}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-foreground-400">—</span>
                          )}
                        </td>
                        <td className="tnum px-2 py-2 text-right text-foreground-600">
                          {line.unitCost != null ? line.unitCost.toFixed(2) : '—'}
                          {spec.frozen && line.calcCost != null && line.unitCost != null && Math.abs(line.calcCost - line.unitCost) >= 0.01 && <span className="block text-[10px] text-accent-700" title={t('sp.nowCostHint')}>{t('sp.now', { value: line.calcCost.toFixed(2) })}</span>}
                        </td>
                        <td className="px-2 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            disabled={!editable}
                            value={draft[key(line.id, 'price')] ?? (line.sale ?? '')}
                            placeholder={line.calcSale != null ? String(line.calcSale) : ''}
                            title={line.manualPrice ? t('sp.manualPriceHint', { value: line.calcSale ?? '—' }) : line.minResale != null ? t('sp.minResaleHint', { value: line.minResale }) : undefined}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setDraft((d) => ({ ...d, [key(line.id, 'price')]: e.target.value }))}
                            onBlur={() => commitLine(line.id, 'price')}
                            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                            aria-label={`${t('specDetail.colSale')} ${line.sku}`}
                            className={`tnum h-6 w-20 rounded border px-1 text-right text-[11.5px] font-medium outline-none focus:border-primary-400 disabled:bg-transparent ${line.manualPrice ? 'border-accent-400 bg-accent-50 text-accent-900' : 'border-background-200 bg-background-50 text-foreground-950'}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-right">
                          <span className="tnum font-medium text-primary-700">{aed(line.lineMargin)}</span>
                          <span className="tnum block text-[10px] text-foreground-400">{pct(line.marginPct)}</span>
                        </td>
                        <td className="px-2 py-2 text-right">
                          <span className={`tnum font-medium ${line.contribution < 0 ? 'text-rose-600' : 'text-foreground-900'}`}>{aed(line.contribution)}</span>
                          <span className="tnum block text-[10px] text-foreground-400">{pct(line.contributionPct)}</span>
                        </td>
                        <td className="px-3 py-2">
                          {editable && (
                            <button type="button" aria-label={t('specDetail.remove', { sku: line.sku })} disabled={busy} onClick={(e) => { e.stopPropagation(); void run(() => specsApi.removeLine(id, line.id)); }} className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-foreground-300 hover:bg-background-100 hover:text-rose-600">
                              <i className="ri-delete-bin-line text-[13px]" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {spec.lines.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-4 py-10 text-center text-[12px] text-foreground-500">
                        {t('specDetail.noLines')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-2 border-t border-background-200 bg-background-100/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[11px] text-foreground-500">{t('specDetail.footer', { items: spec.lines.length, units: num(spec.lines.reduce((s, l) => s + l.qty, 0)), risk: shortLines.length })}</p>
              <div className="flex items-center gap-4 text-[11.5px]">
                <span className="text-foreground-500">
                  {t('specDetail.costLabel')} <span className="tnum font-medium text-foreground-900">{aed(m.costTotal)}</span>
                </span>
                <span className="text-foreground-500">
                  {t('specDetail.saleLabel')} <span className="tnum font-semibold text-foreground-950">{aed(m.saleTotal)}</span>
                </span>
                <span className="text-foreground-500">
                  {t('specDetail.marginLabel')} <span className="tnum font-semibold text-primary-700">{aed(m.productMargin)}</span>
                </span>
              </div>
            </div>
          </div>

          {/* прямые расходы: план по тарифам (переопределяется вручную) и факт после исполнения */}
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold text-foreground-950">{t('sp.costsTitle')}</h2>
                <p className="mt-0.5 max-w-xl text-[11px] leading-4 text-foreground-500">{t('sp.costsSub')}</p>
              </div>
              {m.noCbm.length > 0 && <span className="rounded bg-accent-100 px-2 py-0.5 text-[10.5px] font-medium text-accent-900">{t('sp.noCbm', { skus: m.noCbm.join(', ') })}</span>}
            </div>
            <table className="mt-3 w-full text-[12px]">
              <thead>
                <tr className="border-b border-background-200 text-left text-[10px] uppercase tracking-[0.07em] text-foreground-500">
                  <th className="py-1.5 font-medium">{t('sp.colCost')}</th>
                  <th className="py-1.5 font-medium">{t('sp.basis')}</th>
                  <th className="py-1.5 text-right font-medium">{t('sp.plan')}</th>
                  <th className="py-1.5 text-right font-medium">{t('sp.actual')}</th>
                  <th className="py-1.5 text-right font-medium">{t('sp.delta')}</th>
                </tr>
              </thead>
              <tbody>
                {COST_KINDS.map((kind) => {
                  const plan = spec.costs.plan.find((c) => c.kind === kind);
                  const actual = spec.costs.actual.find((c) => c.kind === kind);
                  const b = (plan?.basis ?? {}) as Record<string, unknown>;
                  const basisText =
                    plan?.overridden
                      ? t('sp.manualOverride') + (plan.comment ? ` · ${plan.comment}` : '')
                      : kind === 'pickup' && Array.isArray(b.partners)
                        ? (b.partners as { partner: string; trips: number; cbm: number }[]).map((g) => `${g.partner}: ${g.trips} × ${aed(Number(b.rate ?? 0))} (${g.cbm.toFixed(2)} m³)`).join(' · ')
                        : kind === 'delivery' && b.cbm != null
                          ? t('sp.deliveryBasis', { cbm: Number(b.cbm).toFixed(2), rate: Number(b.rate ?? 0), min: Number(b.min ?? 0) })
                          : kind === 'installation' && Array.isArray(b.lines)
                            ? (b.lines as { sku: string; qty: number; rate: number }[]).map((l) => `${l.sku}: ${l.qty} × ${l.rate}`).join(' · ')
                            : kind === 'other'
                              ? t('sp.otherHint')
                              : '—';
                  const delta = plan && actual ? actual.amount - plan.amount : null;
                  return (
                    <tr key={kind} className="border-b border-background-200/70 last:border-0">
                      <td className="py-2 font-medium text-foreground-900">{costLabel(kind)}</td>
                      <td className="max-w-[360px] py-2 text-[11px] leading-4 text-foreground-500">{basisText}</td>
                      <td className="py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          disabled={!editable}
                          value={costDraft[`plan:${kind}`] ?? (plan ? plan.amount : '')}
                          placeholder="—"
                          title={plan?.overridden ? t('sp.resetEstimateHint') : t('sp.overrideHint')}
                          onChange={(e) => setCostDraft((d) => ({ ...d, [`plan:${kind}`]: e.target.value }))}
                          onBlur={() => commitCost(kind, 'plan')}
                          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                          aria-label={`${costLabel(kind)} ${t('sp.plan')}`}
                          className={`tnum h-7 w-24 rounded border px-1.5 text-right text-[11.5px] outline-none focus:border-primary-400 disabled:bg-transparent ${plan?.overridden ? 'border-accent-400 bg-accent-50 text-accent-900' : 'border-background-200 bg-background-50'}`}
                        />
                      </td>
                      <td className="py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          disabled={!canEdit}
                          value={costDraft[`actual:${kind}`] ?? (actual ? actual.amount : '')}
                          placeholder="—"
                          title={t('sp.actualHint')}
                          onChange={(e) => setCostDraft((d) => ({ ...d, [`actual:${kind}`]: e.target.value }))}
                          onBlur={() => commitCost(kind, 'actual')}
                          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                          aria-label={`${costLabel(kind)} ${t('sp.actual')}`}
                          className="tnum h-7 w-24 rounded border border-background-200 bg-background-50 px-1.5 text-right text-[11.5px] outline-none focus:border-primary-400 disabled:bg-transparent"
                        />
                      </td>
                      <td className={`tnum py-2 text-right ${delta == null ? 'text-foreground-400' : delta > 0 ? 'text-rose-600' : 'text-primary-700'}`}>{delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(0)}`}</td>
                    </tr>
                  );
                })}
                <tr className="border-t border-background-300 font-semibold">
                  <td className="py-2" colSpan={2}>
                    {t('sp.directCosts')}
                  </td>
                  <td className="tnum py-2 text-right">{aed(m.directCostsPlan)}</td>
                  <td className="tnum py-2 text-right">{m.directCostsActual != null ? aed(m.directCostsActual) : '—'}</td>
                  <td className="tnum py-2 text-right">{m.directCostsActual != null ? `${m.directCostsActual - m.directCostsPlan > 0 ? '+' : ''}${(m.directCostsActual - m.directCostsPlan).toFixed(0)}` : '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground-950">{t('specDetail.totalsTitle')}</h2>
              <i className="ri-calculator-line text-[15px] text-foreground-300" />
            </div>
            <div className="mt-3 space-y-2 text-[12px]">
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">{t('specDetail.salePrice')}</span>
                <span className="tnum font-semibold text-foreground-950">{aed(m.saleTotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">{t('specDetail.costAtPartner')}</span>
                <span className="tnum text-foreground-700">{aed(m.costTotal)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-background-200 pt-2">
                <span className="text-foreground-500">{t('specDetail.marginLabel')}</span>
                <span className="tnum font-semibold text-primary-700">{aed(m.productMargin)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">{t('specDetail.marginPct')}</span>
                <span className={`tnum font-semibold ${m.productMarginPct != null && m.productMarginPct < m.thresholds.minProductMarginPct ? 'text-rose-600' : 'text-foreground-950'}`}>
                  {pct(m.productMarginPct)} <span className="text-[10px] font-normal text-foreground-400">/ {m.thresholds.minProductMarginPct}%</span>
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">{t('sp.directCosts')}</span>
                <span className="tnum text-foreground-700">− {aed(m.directCostsPlan)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-background-200 pt-2">
                <span className="text-foreground-500">{t('sp.contribution')}</span>
                <span className={`tnum font-semibold ${m.contributionPlan < 0 ? 'text-rose-600' : 'text-primary-700'}`}>{aed(m.contributionPlan)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">{t('sp.contributionPct')}</span>
                <span className={`tnum font-semibold ${m.contributionPlanPct != null && m.contributionPlanPct < m.thresholds.minContributionPct ? 'text-rose-600' : 'text-foreground-950'}`}>
                  {pct(m.contributionPlanPct)} <span className="text-[10px] font-normal text-foreground-400">/ {m.thresholds.minContributionPct}%</span>
                </span>
              </div>
              {m.contributionActual != null && (
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500">{t('sp.contributionActual')}</span>
                  <span className={`tnum font-semibold ${m.contributionActual < 0 ? 'text-rose-600' : 'text-foreground-900'}`}>
                    {aed(m.contributionActual)} · {pct(m.contributionActualPct)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-background-200 pt-2">
                <span className="text-foreground-500">{t('specDetail.vat')}</span>
                <span className="tnum text-foreground-700">{aed(m.vatTotal)}</span>
              </div>
              <div className="flex items-center justify-between rounded bg-primary-50 px-2 py-1.5">
                <span className="font-medium text-primary-800">{t('specDetail.totalInclVat')}</span>
                <span className="tnum font-semibold text-primary-900">{aed(m.totalInclVat)}</span>
              </div>
            </div>
            <button type="button" onClick={() => setShowPo(true)} className="mt-3 flex h-8 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 text-[12px] font-semibold text-background-50 hover:bg-primary-600">
              <i className="ri-shopping-bag-3-line text-[13px]" /> {t('specDetail.createPo')}
            </button>
            <p className="mt-1.5 text-center text-[10px] text-foreground-400">{t('specDetail.split', { count: spec.split.filter((g) => !g.own).length })}</p>
          </div>

          {/* согласование ниже порога */}
          <div className={`rounded-lg border p-4 ${m.approvalLevel ? (spec.approval.ok ? 'border-primary-200 bg-primary-50/40' : 'border-accent-300 bg-accent-50') : 'border-background-200 bg-background-50'}`}>
            <div className="flex items-center gap-1.5">
              <i className={`${m.approvalLevel ? 'ri-shield-check-line' : 'ri-checkbox-circle-line'} text-[13px] ${m.approvalLevel ? 'text-accent-800' : 'text-primary-600'}`} />
              <h2 className="text-sm font-semibold text-foreground-950">{t('sp.approvalTitle')}</h2>
            </div>
            {!m.approvalLevel && <p className="mt-1 text-[11.5px] text-foreground-600">{t('sp.approvalNotNeeded')}</p>}
            {m.approvalLevel && (
              <>
                <p className="mt-1 text-[11.5px] leading-4 text-foreground-800">{t(`sp.approvalNeeded.${m.approvalLevel}`)}</p>
                {spec.approval.ok && <p className="mt-1 text-[11.5px] font-medium text-primary-800">{t('sp.approvalOk')}</p>}
                {!spec.approval.ok && spec.approval.pending && (
                  <div className="mt-2 rounded-md border border-background-200 bg-background-50 p-2.5 text-[11.5px]">
                    <p className="text-foreground-700">{t('sp.approvalPending', { who: spec.approval.pending.requestedBy ?? '—' })}</p>
                    {spec.approval.pending.reason && <p className="mt-0.5 italic text-foreground-500">«{spec.approval.pending.reason}»</p>}
                    {canDecide(spec.approval.pending.level) ? (
                      <div className="mt-2 flex gap-2">
                        <button type="button" disabled={busy} onClick={() => void run(() => specsApi.decide(id, spec.approval.pending!.id, true), t('sp.approved'))} className={btnPrimary}>
                          <i className="ri-check-line" /> {t('sp.approve')}
                        </button>
                        <button type="button" disabled={busy} onClick={() => void run(() => specsApi.decide(id, spec.approval.pending!.id, false), t('sp.rejected'))} className={btnGhost}>
                          {t('sp.reject')}
                        </button>
                      </div>
                    ) : (
                      <p className="mt-1 text-[10.5px] text-foreground-400">{t(`sp.whoApproves.${spec.approval.pending.level}`)}</p>
                    )}
                  </div>
                )}
                {!spec.approval.ok && !spec.approval.pending && canEdit && !locked && (
                  <div className="mt-2">
                    <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('sp.reasonPlaceholder')} className={inputCls} />
                    <button type="button" disabled={busy || !reason.trim()} onClick={() => void run(() => specsApi.requestApproval(id, reason), t('sp.approvalRequested')).then((ok) => ok && setReason(''))} className={`${btnPrimary} mt-2 w-full justify-center`}>
                      <i className="ri-send-plane-line" /> {t('sp.requestApproval')}
                    </button>
                    <p className="mt-1 text-[10.5px] leading-4 text-foreground-400">{t(`sp.whoApproves.${m.approvalLevel}`)}</p>
                  </div>
                )}
              </>
            )}
            {spec.approvals.length > 0 && (
              <ul className="mt-3 space-y-1 border-t border-background-200 pt-2 text-[10.5px] text-foreground-500">
                {spec.approvals.slice(0, 5).map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2">
                    <span>
                      v{a.specVersion} · {t(`sp.level.${a.level}`)} · {a.requestedBy ?? '—'}
                    </span>
                    <span className={a.status === 'Approved' ? 'text-primary-700' : a.status === 'Rejected' ? 'text-rose-600' : ''}>
                      {t(`sp.approvalStatus.${a.status}`)}
                      {a.decidedBy ? ` · ${a.decidedBy}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-center gap-1.5">
              <i className="ri-swap-box-line text-[13px] text-primary-600" />
              <h2 className="text-sm font-semibold text-foreground-950">{t('specDetail.findAlt')}</h2>
            </div>
            <p className="mt-0.5 text-[11px] leading-4 text-foreground-500">{activeLine ? t('specDetail.forLine', { sku: activeLine.sku, category: t(`category.${activeLine.category}`, { defaultValue: activeLine.category }) }) : t('specDetail.selectLine')}</p>
            <div className="mt-2.5 space-y-2">
              {alts.map((alt) => (
                <div key={alt.sku} className="rounded-md border border-background-200 p-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded border border-background-200 bg-white">{alt.image ? <img src={alt.image} alt={alt.name} className="h-full w-full object-contain" /> : <i className="ri-image-line text-foreground-300" />}</div>
                    <div className="min-w-0 flex-1">
                      <p className="tnum text-[10px] text-foreground-500">
                        {alt.sku} · {alt.partnerName}
                      </p>
                      <p className="truncate text-[11.5px] font-medium text-foreground-900">{alt.name}</p>
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[10px] text-foreground-500">
                      <span className="tnum">{t('specDetail.inStock', { count: alt.stock })}</span>
                      <span className="tnum font-medium text-foreground-800">{alt.sale != null ? aed(alt.sale) : '—'}</span>
                      <span className="tnum text-primary-700">{alt.marginPct != null ? `${alt.marginPct.toFixed(0)}%` : ''}</span>
                    </div>
                    {editable && activeLine && (
                      <button type="button" disabled={busy} onClick={() => void swap(activeLine.id, alt.sku)} className="cursor-pointer whitespace-nowrap rounded border border-primary-200 bg-primary-50 px-1.5 py-0.5 text-[10px] font-semibold text-primary-800 hover:bg-primary-100">
                        {t('specDetail.swapIn')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {alts.length === 0 && <p className="rounded-md border border-dashed border-background-300 px-2 py-3 text-center text-[11px] text-foreground-500">{t('specDetail.noAlt')}</p>}
            </div>
            <Link to="/catalog" className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary-700 hover:text-primary-800">
              {t('specDetail.searchFull')} <i className="ri-arrow-right-line text-[11px]" />
            </Link>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
            <h2 className="text-sm font-semibold text-foreground-950">{t('specDetail.riskTitle')}</h2>
            <ul className="mt-2 space-y-1.5">
              {shortLines.map((l) => (
                <li key={l.id} className="flex items-start gap-1.5 text-[11px] leading-4 text-accent-900">
                  <i className="ri-error-warning-line mt-0.5 text-[12px]" />
                  <span>{t('specDetail.riskLine', { sku: l.sku, count: l.short })}</span>
                </li>
              ))}
              {(spec.blockers.Quoted ?? []).map((b) => (
                <li key={b} className="flex items-start gap-1.5 text-[11px] leading-4 text-accent-900">
                  <i className="ri-lock-line mt-0.5 text-[12px]" />
                  <span>{t(`sp.blocker.${b}`, { defaultValue: b })}</span>
                </li>
              ))}
              {shortLines.length === 0 && (spec.blockers.Quoted ?? []).length === 0 && (
                <li className="flex items-start gap-1.5 text-[11px] leading-4 text-primary-800">
                  <i className="ri-checkbox-circle-line mt-0.5 text-[12px]" /> {t('specDetail.riskOk')}
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {showPo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/30 p-4" onClick={() => setShowPo(false)}>
          <div className="w-full max-w-lg rounded-lg border border-background-200 bg-background-50 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-sm font-semibold text-foreground-950">{t('specDetail.poTitle')}</h2>
                <p className="mt-0.5 text-[11.5px] text-foreground-500">{t('specDetail.poSub', { id: spec.code })}</p>
              </div>
              <button type="button" onClick={() => setShowPo(false)} aria-label={t('common.close')} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-base" />
              </button>
            </div>
            <div className="mt-4 space-y-2">
              {spec.split
                .filter((g) => !g.own)
                .map((g) => (
                  <div key={g.partner} className="flex items-center justify-between rounded-md border border-background-200 p-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-[12px] font-medium text-foreground-900">{g.partner}</p>
                      <p className="text-[10.5px] text-foreground-500">{t('specDetail.poLines', { count: g.lines, units: g.units })}</p>
                    </div>
                    <span className="tnum text-[12px] font-semibold text-foreground-950">{aed(g.total)}</span>
                  </div>
                ))}
              {spec.split.filter((g) => !g.own).length === 0 && <p className="text-[11.5px] text-foreground-500">{t('specDetail.poEmpty')}</p>}
            </div>
            <div className="mt-3 flex items-center justify-between rounded-md bg-background-100 px-2.5 py-2 text-[11.5px]">
              <span className="text-foreground-600">{t('specDetail.poTotal')}</span>
              <span className="tnum font-semibold text-foreground-950">{aed(spec.split.filter((g) => !g.own).reduce((s, g) => s + g.total, 0))}</span>
            </div>
            <p className="mt-3 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-[11px] leading-4 text-secondary-900">{t('sp.poLater')}</p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowPo(false)} className={btnGhost}>
                {t('specDetail.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} detail={toast.detail} tone={toast.tone} onClose={() => setToast(null)} />}
    </div>
  );
}
